/*
 * Infinite Age — LP2 page behaviour.
 * Vanilla JS, no dependencies. Replaces the live page's jQuery, slick,
 * jquery.marquee, jquery.scroller and bookmarkscroll plugins.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Header: pin to the top once the page scrolls past 110px ---------- */
  function initStickyHeader() {
    var bar = document.querySelector('.top-fix-bar');
    if (!bar) return;

    var ticking = false;
    function update() {
      bar.classList.toggle('fixed-nav', window.scrollY > 110);
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }, { passive: true });
    update();
  }

  /* ---------- Mobile menu (hamburger) ---------- */
  function initMobileMenu() {
    var toggle = document.getElementById('menu-wrapper');
    var burger = document.getElementById('hamburger-menu');
    var list = document.querySelector('#menu-container .menu-list');
    if (!toggle || !burger || !list) return;

    function setOpen(open) {
      burger.classList.toggle('open', open);
      list.classList.toggle('active', open);
      document.body.classList.toggle('overflow-hidden', open);
      toggle.setAttribute('aria-expanded', String(open));
    }

    toggle.setAttribute('role', 'button');
    toggle.setAttribute('tabindex', '0');
    toggle.setAttribute('aria-label', 'Menu');
    toggle.setAttribute('aria-expanded', 'false');

    toggle.addEventListener('click', function () {
      setOpen(!list.classList.contains('active'));
    });
    toggle.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(!list.classList.contains('active'));
      }
    });
    list.addEventListener('click', function (e) {
      if (e.target.closest('.menu-link')) setOpen(false);
    });
  }

  /*
   * ---------- In-page links: smooth scroll that lands exactly on the target ----------
   * Lazy images and the Stamped reviews widget load while the page scrolls and
   * push the target down, so a single scrollTo stops short. Instead, keep
   * re-aiming at the target whenever the layout changes, until it settles.
   */
  function initAnchorLinks() {
    var header = document.querySelector('.header');
    var behavior = reduceMotion ? 'auto' : 'smooth';
    var active = null;

    function targetTop(target) {
      var offset = header ? header.offsetHeight : 0;
      return Math.max(0, Math.round(target.getBoundingClientRect().top + window.scrollY - offset));
    }

    function stop() {
      if (!active) return;
      active.observer.disconnect();
      window.clearTimeout(active.timer);
      ['wheel', 'touchstart', 'keydown'].forEach(function (type) {
        window.removeEventListener(type, stop);
      });
      active = null;
    }

    function scrollToTarget(target) {
      stop();
      var last = targetTop(target);
      window.scrollTo({ top: last, behavior: behavior });

      var observer = new ResizeObserver(function () {
        var top = targetTop(target);
        if (Math.abs(top - last) > 1) {
          last = top;
          window.scrollTo({ top: top, behavior: behavior });
        }
        // keep following for a moment after the last layout change
        window.clearTimeout(active.timer);
        active.timer = window.setTimeout(stop, 1500);
      });
      active = { observer: observer, timer: window.setTimeout(stop, 4000) };
      observer.observe(document.body);

      // the visitor takes over if they scroll themselves
      ['wheel', 'touchstart', 'keydown'].forEach(function (type) {
        window.addEventListener(type, stop, { passive: true });
      });
    }

    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href^="#"]');
      if (!link) return;
      var id = link.getAttribute('href').slice(1);
      var target = id && document.getElementById(id);
      if (!target) return;

      e.preventDefault();
      scrollToTarget(target);
    });
  }

  /* ---------- FAQ accordion: one answer open at a time ---------- */
  function initFaq() {
    var titles = document.querySelectorAll('.faq-box .at-title');
    if (!titles.length) return;

    function panelOf(title) {
      return title.nextElementSibling;
    }

    titles.forEach(function (title) {
      var open = title.classList.contains('active');
      title.setAttribute('role', 'button');
      title.setAttribute('tabindex', '0');
      title.setAttribute('aria-expanded', String(open));
      panelOf(title).hidden = !open;
    });

    function toggle(title) {
      var willOpen = !title.classList.contains('active');
      titles.forEach(function (t) {
        var open = t === title && willOpen;
        t.classList.toggle('active', open);
        t.setAttribute('aria-expanded', String(open));
        panelOf(t).hidden = !open;
      });
    }

    titles.forEach(function (title) {
      title.addEventListener('click', function () { toggle(title); });
      title.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle(title);
        }
      });
    });
  }

  /*
   * ---------- Image strip slider ----------
   * Same settings as the live slick config: autoplay every 3s, one slide per
   * step, 6 / 5 / 3 / 2 slides visible at >1020 / >767 / >479 / smaller widths.
   * Loops seamlessly by cloning the first "visible" slides onto the end.
   */
  function initStripSlider() {
    var slider = document.querySelector('.strip2sldr');
    if (!slider) return;

    var slides = Array.prototype.slice.call(slider.children);
    var count = slides.length;
    if (!count) return;

    var track = document.createElement('div');
    track.className = 'strip2sldr__track';
    slides.forEach(function (slide) {
      slide.className = 'strip2sldr__slide';
      track.appendChild(slide);
    });
    slides.forEach(function (slide) {
      var clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    });
    slider.appendChild(track);

    function perView() {
      var w = window.innerWidth;
      if (w <= 479) return 2;
      if (w <= 767) return 3;
      if (w <= 1020) return 5;
      return 6;
    }

    var index = 0;
    function applyLayout() {
      slider.style.setProperty('--per-view', perView());
    }
    // the track is as wide as the slider, so each step is 100% / slides-per-view
    function goTo(i, animate) {
      track.style.transition = animate ? '' : 'none';
      index = i;
      track.style.transform = 'translateX(' + (-index * 100 / perView()) + '%)';
    }

    track.addEventListener('transitionend', function () {
      if (index >= count) goTo(index - count, false);
    });

    applyLayout();
    goTo(0, false);
    window.addEventListener('resize', function () {
      applyLayout();
      goTo(index % count, false);
    });

    if (reduceMotion) return;

    var timer = null;
    function start() {
      if (timer) return;
      timer = window.setInterval(function () {
        // re-enable the transition after a "none" jump
        void track.offsetWidth;
        goTo(index + 1, true);
      }, 3000);
    }
    function stop() {
      window.clearInterval(timer);
      timer = null;
    }
    slider.addEventListener('mouseenter', stop);
    slider.addEventListener('mouseleave', start);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });
    start();
  }

  /* ---------- Benefits ticker (mobile): continuous right-to-left scroll ---------- */
  function initTicker() {
    var list = document.getElementById('news');
    if (!list || reduceMotion) return;

    // duplicate the items once so the loop has no gap
    Array.prototype.slice.call(list.children).forEach(function (item) {
      var clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      list.appendChild(clone);
    });
    list.classList.add('news--ticking');
  }

  /* ---------- Footer year ---------- */
  function initYear() {
    var year = String(new Date().getFullYear());
    document.querySelectorAll('[data-year]').forEach(function (el) {
      el.textContent = year;
    });
  }

  initStickyHeader();
  initMobileMenu();
  initAnchorLinks();
  initFaq();
  initStripSlider();
  initTicker();
  initYear();
})();
